import React from 'react';
import { useQuery } from '@apollo/client';
import { useStore } from 'context/store-context';
import {
  GET_RETURN_BILLING_PRICING_STATUS,
  GET_RETURN_BILLING_SUMMARY,
} from 'gql/queries';
import ReturnBillingSummary, {
  ReturnBillingPricingStatusQueryResult,
  ReturnBillingSummaryQueryResult,
} from './ReturnBillingSummary';
import {
  RequestReportDateRange,
  useRequestReportDateRange,
} from './requestReportDateRange';

const billingBuckets: Array<keyof ReturnBillingSummaryQueryResult> = [
  'liveLabels',
  'liveKeepItems',
  'shadowLabels',
  'shadowKeepItems',
  'pending',
  'failed',
  'invalidCurrency',
];
const completeBillingData = (data?: ReturnBillingSummaryQueryResult) =>
  billingBuckets.every((key) => {
    const bucket = data?.[key]?.aggregate;
    if (
      bucket?.count == null ||
      !Number.isFinite(Number(bucket.count)) ||
      Number(bucket.count) < 0
    )
      return false;
    if (
      Number(bucket.count) === 0 ||
      ['pending', 'failed', 'invalidCurrency'].includes(key)
    )
      return true;
    return ['amount', 'carrierCost', 'serviceFee'].every((field) => {
      const value =
        bucket.sum?.[field as 'amount' | 'carrierCost' | 'serviceFee'];
      return (
        value != null && Number.isFinite(Number(value)) && Number(value) >= 0
      );
    });
  });

type BillingDateRange = Pick<
  ReturnType<typeof useRequestReportDateRange>,
  'startDateTime' | 'endDateTime'
>;

function BillingContent({
  range,
  children,
}: {
  range: BillingDateRange;
  children?: React.ReactNode;
}) {
  const { storeId } = useStore();
  const pricing = useQuery<ReturnBillingPricingStatusQueryResult>(
    GET_RETURN_BILLING_PRICING_STATUS,
    {
      variables: { storeId },
      skip: !storeId,
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    },
  );
  const billing = useQuery<ReturnBillingSummaryQueryResult>(
    GET_RETURN_BILLING_SUMMARY,
    {
      variables: {
        storeId,
        startDate: range.startDateTime,
        endDate: range.endDateTime,
      },
      skip: !storeId,
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    },
  );
  const rollout = pricing.data?.returnBillingPricingStatus;
  const validRollout =
    rollout?.storeId === storeId &&
    rollout?.pricingVersion != null &&
    rollout?.rolloutMode != null;
  const rolloutError =
    pricing.error ||
    (!pricing.loading && !validRollout
      ? new Error('Return billing configuration is unavailable.')
      : null);
  const billingError =
    billing.error ||
    (!billing.loading && !completeBillingData(billing.data)
      ? new Error('Return billing data is incomplete.')
      : null);

  if (!storeId)
    return (
      <p className="text-sm text-gray-600">Select a store to view billing.</p>
    );
  return (
    <div className="space-y-5">
      {children}
      <p className="text-sm leading-6 text-gray-600">
        Shows recorded shipping and legacy Keep Your Item billing events. An
        exchange appears only when it has a recorded billing event; replacement
        charges, refunds, and store credit are not included.
      </p>
      <ReturnBillingSummary
        data={billing.data}
        loading={billing.loading}
        error={billingError}
        rollout={validRollout ? rollout : null}
        rolloutLoading={pricing.loading}
        rolloutError={rolloutError}
      />
      {(rolloutError || billingError) && (
        <button
          type="button"
          onClick={() => {
            void pricing.refetch();
            void billing.refetch();
          }}
          className="text-sm font-medium text-blue-700 hover:underline"
        >
          Retry billing
        </button>
      )}
    </div>
  );
}

function StandaloneBilling() {
  const range = useRequestReportDateRange();
  return (
    <BillingContent range={range}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Return &amp; exchange billing
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Current pricing and recorded billing events within the selected
            dates.
          </p>
        </div>
        <RequestReportDateRange {...range} />
      </div>
    </BillingContent>
  );
}

export function RequestBilling({
  dateRange,
}: {
  dateRange?: BillingDateRange;
}) {
  return dateRange ? (
    <BillingContent range={dateRange} />
  ) : (
    <StandaloneBilling />
  );
}
